import React, { useMemo, useState } from "react";
import { CheckCircle2, XCircle, Monitor, Smartphone } from "lucide-react";

interface SeoAnalyzerProps {
  title: string;
  description: string;
  content: string;
  focusKeywords: string; // Comma separated
  canonicalUrl: string;
  siteUrl?: string;
  slug: string;
  folderPath: string; // e.g., "products" or "blog"
}

export function SeoAnalyzer({
  title = "",
  description = "",
  content = "",
  focusKeywords = "",
  canonicalUrl = "",
  siteUrl = "https://subaashghor.com",
  slug = "example-slug",
  folderPath = "products",
}: SeoAnalyzerProps) {
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");

  // Parse keywords: up to 5 keywords
  const keywordsList = useMemo(() => {
    return focusKeywords
      .split(",")
      .map((k) => k.trim().toLowerCase())
      .filter((k) => k.length > 0)
      .slice(0, 5);
  }, [focusKeywords]);

  // Strip HTML helper
  const cleanText = useMemo(() => {
    return content.replace(/<\/?[^>]+(>|$)/g, " ");
  }, [content]);

  const wordCount = useMemo(() => {
    const words = cleanText.trim().split(/\s+/).filter(Boolean);
    return words.length;
  }, [cleanText]);

  // Perform checks
  const analysis = useMemo(() => {
    const checks: { ok: boolean; label: string }[] = [];
    let scorePoints = 0;
    let totalPossible = 0;

    const addCheck = (ok: boolean, label: string, points: number) => {
      checks.push({ ok, label });
      totalPossible += points;
      if (ok) scorePoints += points;
    };

    const titleLower = title.toLowerCase();
    const descLower = description.toLowerCase();
    const contentLower = cleanText.toLowerCase();

    // 1. Title Length Check
    const titleLen = title.length;
    addCheck(
      titleLen > 0 && titleLen <= 60,
      `Title under 60 characters (Current: ${titleLen})`,
      10,
    );

    // 2. Meta Description Length Check
    const descLen = description.length;
    addCheck(
      descLen > 0 && descLen <= 160,
      `Meta Description under 160 characters (Current: ${descLen})`,
      10,
    );

    // If keywords list is empty, add fallback checklist items
    if (keywordsList.length === 0) {
      addCheck(false, "Add at least one focus keyword for dynamic analysis", 0);
    } else {
      // 3. Focus keyword in Title
      const kwInTitle = keywordsList.some((kw) => titleLower.includes(kw));
      addCheck(kwInTitle, "Focus keyword present in SEO Title", 15);

      // 4. Focus keyword in Meta Description
      const kwInDesc = keywordsList.some((kw) => descLower.includes(kw));
      addCheck(kwInDesc, "Focus keyword present in Meta Description", 15);

      // 5. Keyword in first 10% of content
      const first10Percent = contentLower.substring(
        0,
        Math.floor(contentLower.length * 0.1) || 200,
      );
      const kwInFirst10 = keywordsList.some((kw) => first10Percent.includes(kw));
      addCheck(kwInFirst10, "Focus keyword present in the first 10% of the content", 10);

      // 6. Focus keyword in headings (H1, H2, H3)
      const hasHeadingMatches = () => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        const headings = Array.from(doc.querySelectorAll("h1, h2, h3")).map(
          (h) => h.textContent?.toLowerCase() || "",
        );
        return keywordsList.some((kw) => headings.some((heading) => heading.includes(kw)));
      };
      addCheck(hasHeadingMatches(), "Focus keyword present in H1, H2, or H3 headings", 10);

      // 7. Keyword Density check (0.5% to 2.5%)
      let densityOk = false;
      let densityMsg = "Keyword density between 0.5%–2.5%";
      if (wordCount > 0) {
        const densities = keywordsList.map((kw) => {
          const regex = new RegExp(`\\b${kw}\\b`, "gi");
          const matches = contentLower.match(regex);
          const count = matches ? matches.length : 0;
          return (count / wordCount) * 100;
        });
        const avgDensity = densities.reduce((a, b) => a + b, 0) / densities.length;
        densityOk = avgDensity >= 0.5 && avgDensity <= 2.5;
        densityMsg = `Keyword density is ${densityOk ? "healthy" : "outside range"} (Avg: ${avgDensity.toFixed(2)}%)`;
      }
      addCheck(densityOk, densityMsg, 15);
    }

    // 8. Image Alt Tags check
    let hasAltCheck = true;
    let missingAlts = 0;
    if (content.includes("<img")) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");
      const imgs = Array.from(doc.querySelectorAll("img"));
      missingAlts = imgs.filter((img) => !img.getAttribute("alt")?.trim()).length;
      hasAltCheck = missingAlts === 0;
    }
    addCheck(
      hasAltCheck,
      hasAltCheck
        ? "All images in content have Alt Text"
        : `${missingAlts} image(s) missing Alt Text in content`,
      10,
    );

    // 9. Internal Links check (at least 2 internal links)
    let internalLinksCount = 0;
    if (content.includes("<a")) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, "text/html");
      const anchors = Array.from(doc.querySelectorAll("a"));
      internalLinksCount = anchors.filter((a) => {
        const href = a.getAttribute("href") || "";
        return href.startsWith("/") || href.includes("subaashghor.com");
      }).length;
    }
    addCheck(
      internalLinksCount >= 2,
      `Internal links added (Current: ${internalLinksCount} / Required: 2)`,
      15,
    );

    const finalScore = totalPossible > 0 ? Math.round((scorePoints / totalPossible) * 100) : 0;

    return {
      checks,
      score: finalScore,
    };
  }, [title, description, content, keywordsList, cleanText, wordCount]);

  // Color selection based on score
  const scoreColor = useMemo(() => {
    if (analysis.score >= 80) return { stroke: "oklch(0.78 0.12 85)", text: "text-emerald-400" };
    if (analysis.score >= 50) return { stroke: "oklch(0.68 0.15 45)", text: "text-amber-500" };
    return { stroke: "oklch(0.55 0.18 20)", text: "text-red-500" };
  }, [analysis.score]);

  return (
    <div className="space-y-6">
      {/* 1. Score and Title */}
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row border-b border-gold/10 pb-4">
        <div className="space-y-1">
          <h4 className="font-serif text-base text-gold">SEO Analysis & Score</h4>
          <p className="text-xs text-muted-foreground">Word Count: {wordCount} words</p>
        </div>
        <div className="relative h-20 w-20">
          <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="44"
              stroke="oklch(0.32 0.05 25)"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="50"
              cy="50"
              r="44"
              stroke={scoreColor.stroke}
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${(analysis.score / 100) * 276} 276`}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={`font-display text-2xl font-bold text-gold`}>{analysis.score}</div>
          </div>
        </div>
      </div>

      {/* 2. Snippet Preview Toggle and Preview Card */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gold-soft font-semibold">
            Google Snippet Preview
          </div>
          <div className="flex gap-1 border border-gold/15 rounded p-0.5 bg-accent/10">
            <button
              type="button"
              onClick={() => setPreviewMode("desktop")}
              className={`p-1 rounded cursor-pointer transition ${
                previewMode === "desktop" ? "bg-gold/20 text-gold" : "text-muted-foreground"
              }`}
              title="Desktop Preview"
            >
              <Monitor className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode("mobile")}
              className={`p-1 rounded cursor-pointer transition ${
                previewMode === "mobile" ? "bg-gold/20 text-gold" : "text-muted-foreground"
              }`}
              title="Mobile Preview"
            >
              <Smartphone className="h-3 w-3" />
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-gold/15 bg-background/50 p-4 transition-all duration-300">
          {previewMode === "desktop" ? (
            <div className="max-w-[600px] space-y-1">
              <div className="text-xs text-muted-foreground truncate font-sans">
                {siteUrl.replace(/^https?:\/\//, "")} › {folderPath} › {slug}
              </div>
              <div className="font-sans text-lg text-[#8ab4f8] hover:underline cursor-pointer truncate font-medium leading-tight">
                {title || "Please enter an SEO title..."}
              </div>
              <p className="text-sm text-foreground/80 font-sans line-clamp-2 leading-relaxed">
                {description || "Please enter a meta description..."}
              </p>
            </div>
          ) : (
            <div className="max-w-[360px] space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground font-sans">
                <div className="h-4 w-4 rounded-full bg-foreground/10 flex items-center justify-center font-bold text-[8px] text-gold">
                  SG
                </div>
                <div>
                  <div className="text-foreground/90 font-medium leading-none">Subaashghor</div>
                  <div className="text-[10px] leading-none mt-0.5">
                    {siteUrl}/{folderPath}/{slug}
                  </div>
                </div>
              </div>
              <div className="font-sans text-base text-[#8ab4f8] hover:underline cursor-pointer leading-tight font-medium">
                {title || "Please enter an SEO title..."}
              </div>
              <p className="text-xs text-foreground/80 font-sans line-clamp-3 leading-relaxed">
                {description || "Please enter a meta description..."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 3. Live Checklist */}
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gold-soft font-semibold">
          Optimization Checklist
        </div>
        <ul className="space-y-2 text-xs">
          {analysis.checks.map((c, i) => (
            <li key={i} className="flex items-start gap-2.5">
              {c.ok ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
              ) : (
                <XCircle className="h-4 w-4 mt-0.5 text-red-400 shrink-0" />
              )}
              <span className={c.ok ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
